import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button, ErrorState, LoadingState } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { followSeries, getSeriesById, getSeriesEpisodes, unfollowSeries } from '../services/seriesService';
import Avatar from '../components/ui/Avatar';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

const SeriesDetailPage = () => {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [series, setSeries] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const [s, e] = await Promise.all([getSeriesById(seriesId), getSeriesEpisodes(seriesId, 100)]);
      if (cancelled) return;
      if (!s.success) {
        setError(s.error || 'No se pudo cargar la serie');
        setLoading(false);
        return;
      }
      setSeries(s.series);
      setEpisodes(e.success ? (e.episodes || []) : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [seriesId]);

  // Cargar estado real de follow desde Firestore
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.uid || !seriesId) {
        setFollowing(false);
        return;
      }
      try {
        const followerRef = doc(db, 'series', seriesId, 'followers', currentUser.uid);
        const snap = await getDoc(followerRef);
        if (cancelled) return;
        setFollowing(snap.exists());
      } catch {
        if (!cancelled) setFollowing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid, seriesId]);

  const handleToggleFollow = async () => {
    if (!currentUser) return;
    if (!series?.id) return;
    setFollowBusy(true);
    try {
      if (following) {
        const res = await unfollowSeries(series.id);
        if (res.success) {
          setFollowing(false);
          setSeries((prev) =>
            prev ? { ...prev, followersCount: Math.max(0, (prev.followersCount || 0) - 1) } : prev
          );
        }
      } else {
        const res = await followSeries(series.id);
        if (res.success) {
          setFollowing(true);
          setSeries((prev) =>
            prev ? { ...prev, followersCount: (prev.followersCount || 0) + 1 } : prev
          );
        }
      }
    } finally {
      setFollowBusy(false);
    }
  };

  return (
    <Layout>
      <div className="py-6">
        <div className="container-custom">
          {loading && <LoadingState message="Cargando serie..." />}
          {error && <ErrorState title="No se pudo cargar" message={error} />}

          {!loading && !error && series && (
            <>
              <div className="flex flex-col lg:flex-row gap-6 mb-8">
                <div className="w-full lg:w-72 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-700">
                    {series.coverUrl ? (
                      <img src={series.coverUrl} alt={series.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-purple-600" />
                    )}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{series.title}</h1>
                  {series.description && (
                    <p className="mt-3 text-gray-700 dark:text-gray-300">{series.description}</p>
                  )}

                  <div className="mt-4 flex items-center gap-3">
                    <Avatar
                      src={series.ownerPhotoURL}
                      alt={series.ownerName}
                      fallbackText={series.ownerName}
                      size="md"
                    />
                    <Link
                      to={`/profile/${series.ownerId}`}
                      className="text-sm font-medium text-gray-900 dark:text-white hover:underline"
                    >
                      {series.ownerName}
                    </Link>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {series.followersCount || 0} seguidores
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {series.episodesCount || episodes.length} episodios
                    </span>
                  </div>

                  <div className="mt-6 flex gap-2">
                    {currentUser && currentUser.uid !== series.ownerId && (
                      <Button variant={following ? 'outline' : 'gradient'} onClick={handleToggleFollow} loading={followBusy}>
                        {following ? 'Siguiendo' : 'Seguir serie'}
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => window.history.back()}>
                      Volver
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Episodios</h2>
              </div>

              {episodes.length === 0 ? (
                <div className="text-gray-600 dark:text-gray-400">Aún no hay episodios.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {episodes.map((ep) => {
                    const first = ep.media?.[0]?.url;
                    return (
                      <button
                        key={ep.id}
                        type="button"
                        onClick={() => navigate(`/post/${ep.id}`)}
                        className="text-left bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden hover:shadow-lg transition-all"
                      >
                        <div className="aspect-[4/3] bg-gray-100 dark:bg-slate-700">
                          {first ? (
                            <img src={first} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-purple-600" />
                          )}
                        </div>
                        <div className="p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Episodio {ep.episodeNumber || '-'}
                          </div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 mt-1">
                            {ep.caption || 'Sin título'}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

    </Layout>
  );
};

export default SeriesDetailPage;

